import { useQueryClient } from '@tanstack/react-query';
import { useConfirmation } from 'components/modals/ConfirmationContext';
import { Permission } from 'interfaces/permissionOption';
import { Study, StudyWorkflowStepApproval } from 'interfaces/study';
import { useMemo, useState } from 'react';
import { usePermissions } from 'utils/usePermissions';
import { recordStudyWorkflowStepApproval } from './workflowStepApprovalClient';

import { ImageAnalysisTaskStatus } from './studyWorkflowJobStatus';
import { StudyWorkflowStepDefinition } from './studyWorkflowStepTypes';
import { buildWorkflowStepCompletions, WorkflowModalityCount } from './workflowStepCompletion';

export const MARK_DONE_FAILURE_MESSAGE = 'This step could not be marked done. The current state is unchanged.';

export const useWorkflowStepDoneState = ({
  study,
  studyId,
  steps,
  isLoadingUploads,
  isUploadListError,
  hasLoadedSlideInventory,
  hasLoadedCaseImages,
  isProceduresError,
  uploadProgress,
  studyInventory,
  missingStainingModalityCount,
  caseCount,
  hasInitialImageAnalysisStatus,
  showImageAnalysisStatusError,
  canViewJobs,
  allSlideCount,
  multiplexSlideCount,
  imageAnalysisTaskStatuses,
  panelApproval,
  rnaSpotDetectionTaskStatus,
}: {
  study?: Study;
  studyId: string;
  steps: StudyWorkflowStepDefinition[];
  isLoadingUploads: boolean;
  isUploadListError: boolean;
  hasLoadedSlideInventory: boolean;
  hasLoadedCaseImages: boolean;
  isProceduresError: boolean;
  uploadProgress: { uploadedSlideCount: number; activeIngestCount: number; ingestedSlideCount: number };
  studyInventory: {
    slidesWithStainingMetadataCount: number;
    slideRecordCount: number;
    modalityCounts: WorkflowModalityCount[];
    ungroupedSlideCount: number;
    caseSlideCount: number;
  };
  missingStainingModalityCount: number;
  caseCount: number;
  hasInitialImageAnalysisStatus: boolean;
  showImageAnalysisStatusError: boolean;
  canViewJobs: boolean;
  allSlideCount: number;
  multiplexSlideCount: number;
  imageAnalysisTaskStatuses: ImageAnalysisTaskStatus[];
  panelApproval: {
    isLoading: boolean;
    isError: boolean;
    isApproved: boolean;
    hasSavedPanels: boolean;
    data?: { approvedSlidesCount?: number; totalSlidesCount?: number };
  };
  rnaSpotDetectionTaskStatus: ImageAnalysisTaskStatus;
}) => {
  const { hasPermission } = usePermissions();
  const confirmation = useConfirmation();
  const queryClient = useQueryClient();
  const [expandedDoneStepIds, setExpandedDoneStepIds] = useState<Record<string, boolean>>({});
  const [recordedApprovals, setRecordedApprovals] = useState<StudyWorkflowStepApproval[]>([]);
  const [completionError, setCompletionError] = useState<{ stepId: string; message: string }>();
  const [savingStepId, setSavingStepId] = useState<string>();
  const [savingAction, setSavingAction] = useState<'approved' | 'revoked'>();
  const workflowStepApprovals = useMemo(() => {
    const persisted = study?.settings?.workflowStepApprovals || [];
    const persistedKeys = new Set(
      persisted.map((approval) => `${approval.stepKey}:${approval.approvedAt}:${approval.action}`)
    );
    return [
      ...persisted,
      ...recordedApprovals.filter(
        (approval) => !persistedKeys.has(`${approval.stepKey}:${approval.approvedAt}:${approval.action}`)
      ),
    ];
  }, [recordedApprovals, study?.settings?.workflowStepApprovals]);
  const completions = useMemo(
    () =>
      buildWorkflowStepCompletions({
        approvals: workflowStepApprovals,
        approverNames: study?.workflowStepApproverNames,
        canEditStudy: hasPermission(Permission.EditStudyNameAndDescription),
        datasetPreparation: {
          isReady: !isLoadingUploads && !isUploadListError && hasLoadedSlideInventory,
          isLoading: isLoadingUploads || !hasLoadedSlideInventory,
          uploadedSlideCount: uploadProgress.uploadedSlideCount,
          activeIngestCount: uploadProgress.activeIngestCount,
          ingestedSlideCount: uploadProgress.ingestedSlideCount,
          slidesWithStainingMetadataCount: studyInventory.slidesWithStainingMetadataCount,
          slideRecordCount: studyInventory.slideRecordCount,
          modalityCounts: studyInventory.modalityCounts,
        },
        caseOrganization: {
          isReady: hasLoadedSlideInventory && !isProceduresError,
          slideRecordCount: studyInventory.slideRecordCount,
          ungroupedSlideCount: studyInventory.ungroupedSlideCount,
          missingStainingModalityCount,
          caseSlideCount: studyInventory.caseSlideCount,
          caseCount,
        },
        caseImageAnalysis: {
          isReady:
            hasLoadedCaseImages && !isProceduresError && hasInitialImageAnalysisStatus && !showImageAnalysisStatusError,
          isLoading:
            !hasLoadedCaseImages ||
            (allSlideCount > 0 && canViewJobs && !hasInitialImageAnalysisStatus && !showImageAnalysisStatusError),
          blockedReason: canViewJobs ? undefined : 'Job viewing permission is required.',
          noSlidesWithModality: allSlideCount === 0,
          tasks: imageAnalysisTaskStatuses,
        },
        markerAssignments: {
          isReady: !panelApproval.isLoading && !panelApproval.isError,
          isApproved: panelApproval.isApproved,
          hasSavedPanels: panelApproval.hasSavedPanels,
          approvedSlidesCount: panelApproval.data?.approvedSlidesCount || 0,
          totalSlidesCount: panelApproval.data?.totalSlidesCount || 0,
        },
        markerNormalization: {
          isReady: hasLoadedCaseImages && !isProceduresError,
          isLoading: !hasLoadedCaseImages,
          multiplexSlideCount,
        },
        rnaProbes: {
          isApplicable: steps.some((step) => step.id === 'rna-probes'),
          isReady:
            hasLoadedCaseImages &&
            !isProceduresError &&
            (multiplexSlideCount === 0 || (hasInitialImageAnalysisStatus && !showImageAnalysisStatusError)),
          isLoading:
            !hasLoadedCaseImages ||
            (multiplexSlideCount > 0 && canViewJobs && !hasInitialImageAnalysisStatus && !showImageAnalysisStatusError),
          blockedReason: multiplexSlideCount > 0 && !canViewJobs ? 'Job viewing permission is required.' : undefined,
          noMultiplexSlides: multiplexSlideCount === 0,
          task: rnaSpotDetectionTaskStatus,
        },
      }),
    [
      allSlideCount,
      canViewJobs,
      caseCount,
      hasInitialImageAnalysisStatus,
      hasLoadedCaseImages,
      hasLoadedSlideInventory,
      hasPermission,
      imageAnalysisTaskStatuses,
      isLoadingUploads,
      isProceduresError,
      isUploadListError,
      missingStainingModalityCount,
      multiplexSlideCount,
      panelApproval.data?.approvedSlidesCount,
      panelApproval.data?.totalSlidesCount,
      panelApproval.hasSavedPanels,
      panelApproval.isApproved,
      panelApproval.isError,
      panelApproval.isLoading,
      rnaSpotDetectionTaskStatus,
      showImageAnalysisStatusError,
      steps,
      study?.workflowStepApproverNames,
      studyInventory.caseSlideCount,
      studyInventory.modalityCounts,
      studyInventory.slideRecordCount,
      studyInventory.slidesWithStainingMetadataCount,
      studyInventory.ungroupedSlideCount,
      uploadProgress.activeIngestCount,
      uploadProgress.ingestedSlideCount,
      uploadProgress.uploadedSlideCount,
      workflowStepApprovals,
    ]
  );

  const recordStepDecision = async ({
    stepId,
    action,
    summary,
    digest,
    failureMessage,
  }: {
    stepId: string;
    action: 'approved' | 'revoked';
    summary?: string;
    digest?: string;
    failureMessage: string;
  }): Promise<boolean> => {
    setSavingAction(action);
    setSavingStepId(stepId);
    setCompletionError(undefined);
    try {
      const recorded = await recordStudyWorkflowStepApproval({
        studyId,
        stepKey: stepId,
        action,
        stateDigest: digest,
        stateSummary: summary,
      });
      setRecordedApprovals((current) => [...current, recorded]);
      setExpandedDoneStepIds((current) => ({ ...current, [stepId]: false }));
      await queryClient.invalidateQueries({ queryKey: ['study', studyId], exact: false });
      return true;
    } catch {
      setCompletionError({ stepId, message: failureMessage });
      return false;
    } finally {
      setSavingAction(undefined);
      setSavingStepId(undefined);
    }
  };

  const markStepDone = async (
    stepId: string,
    stepLabel: string,
    summary: string,
    digest: string
  ): Promise<'approved' | 'cancelled' | 'failed'> => {
    const confirmed = await confirmation({
      title: `Mark ${stepLabel} as done`,
      text: `Mark ${stepLabel} as done with this state: ${summary}?`,
      confirmButtonProps: { title: 'Mark as done', color: 'primary', variant: 'contained' },
      cancelButtonProps: { title: 'Cancel', color: 'inherit', variant: 'outlined' },
    });
    if (!confirmed) {
      return 'cancelled';
    }
    const isRecorded = await recordStepDecision({
      stepId,
      action: 'approved',
      summary,
      digest,
      failureMessage: MARK_DONE_FAILURE_MESSAGE,
    });
    return isRecorded ? 'approved' : 'failed';
  };

  return {
    completions,
    savingAction,
    completionError,
    savingStepId,
    expandedDoneStepIds,
    setExpandedDoneStepIds,
    markStepDone,
    reopenStep: (stepId: string) =>
      recordStepDecision({
        stepId,
        action: 'revoked',
        failureMessage: 'This step could not be reopened. It is still marked done.',
      }),
  };
};
